import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { setBreadcrumbName } from '../lib/breadcrumbNames';
import { useT } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ListInput from './ListInput';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import HelpHint from './HelpHint';
import XpnsrEmpty from './XpnsrEmpty';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Plus,
  Edit,
  Trash2,
  Filter,
  Globe,
  Smartphone,
  Play,
  Pause,
  Brain,
  Star,
  Copy,
  Download,
  Upload,
  RefreshCw,
  ListChecks,
  Archive,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const initialForm = {
  name: '',
  stream_type: 'normal',
  position: 0,
  weight: 100,
  action_type: 'redirect',
  action_options: { redirect_url: '' },
  countries: [], cities: [], devices: [], browsers: [],
  operating_systems: [], languages: [], connection_types: [], time_ranges: [],
  filter_relation: 'and',
  countries_mode: 'is', cities_mode: 'is', devices_mode: 'is', browsers_mode: 'is',
  operating_systems_mode: 'is', languages_mode: 'is', connection_types_mode: 'is',
  time_ranges_mode: 'is',
  date_ranges: [], date_ranges_mode: 'is',
  regions: [], regions_mode: 'is',
  ip_addresses: [], ip_addresses_mode: 'is',
  user_agents: [], user_agents_mode: 'is',
  isps: [], isps_mode: 'is',
  operators: [], operators_mode: 'is',
  search_engines: [], search_engines_mode: 'is',
  x_requested_with: [], x_requested_with_mode: 'is',
  bot: null, proxy: null, ip_version: null,
  unique_clicks: null,
  click_limit: null,
  click_limit_per_hour: null,
  click_limit_per_24h: null,
  parameter_filters: {},
  parameter_filters_exclude: {},
  landing_page_ids: [],
  offer_ids: [],
  offer_selection_moment: 'after'
};

const DOW = [
  ['Mon', 0],
  ['Tue', 1],
  ['Wed', 2],
  ['Thu', 3],
  ['Fri', 4],
  ['Sat', 5],
  ['Sun', 6],
];

// Allow rules render as `name=v1, v2`; exclude rules as
// `name!=v1, v2`. Both live in one textarea, one rule per line.
const paramFiltersToText = (pf, pfExclude) => {
  const allow = Object.entries(pf || {}).map(
    ([k, v]) => `${k}=${(v || []).join(', ')}`
  );
  const block = Object.entries(pfExclude || {}).map(
    ([k, v]) => `${k}!=${(v || []).join(', ')}`
  );
  return [...allow, ...block].join('\n');
};

const textToParamFilters = (text) => {
  const include = {};
  const exclude = {};
  (text || '').split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const neq = trimmed.indexOf('!=');
    const eq = trimmed.indexOf('=');
    let key;
    let rest;
    let target;
    if (neq !== -1 && neq <= eq) {
      key = trimmed.slice(0, neq).trim();
      rest = trimmed.slice(neq + 2);
      target = exclude;
    } else if (eq !== -1) {
      key = trimmed.slice(0, eq).trim();
      rest = trimmed.slice(eq + 1);
      target = include;
    } else {
      return;
    }
    if (!key) return;
    target[key] = rest
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  });
  return { include, exclude };
};

export default function Streams() {
  const { campaignId } = useParams();
  const fileInputRef = React.useRef(null);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceFind, setReplaceFind] = useState('');
  const [replaceWith, setReplaceWith] = useState('');
  const [favOpen, setFavOpen] = useState(false);
  const [favStreams, setFavStreams] = useState([]);
  const { user } = useAuth();
  const { t } = useT();
  const [streams, setStreams] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [landingPages, setLandingPages] = useState([]);
  const [offers, setOffers] = useState([]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingStream, setEditingStream] = useState(null);

  const [formData, setFormData] = useState(initialForm);
  const [paramFiltersText, setParamFiltersText] = useState('');
  const [savingSplit, setSavingSplit] = useState(false);

  useEffect(() => {
    if (campaignId) {
      fetchCampaign();
      fetchStreams();
      fetchLandingPages();
      fetchOffers();
    }
  }, [campaignId]);

  const fetchCampaign = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}`);
      setCampaign(response.data);
      // Let the breadcrumb topbar show the campaign name instead of the raw id.
      setBreadcrumbName(response.data?.id, response.data?.name);
    } catch (error) {
      console.error(error);
      toast.error(t('Failed to load campaign'));
    }
  };

  const toggleFavourite = async (stream) => {
    try {
      const res = await axios.put(`${API}/streams/${stream.id}`, { is_favourite: !stream.is_favourite });
      setStreams((prev) => prev.map((s) => (s.id === stream.id ? res.data : s)));
      toast.success(stream.is_favourite ? t('Removed from favourites') : t('Added to favourites'));
    } catch (_e) {
      toast.error(t('Failed to update stream'));
    }
  };
  const cloneStream = async (stream) => {
    try {
      const res = await axios.post(`${API}/streams/${stream.id}/clone`);
      setStreams((prev) => [...prev, res.data]);
      toast.success(t('Stream cloned'));
    } catch (_e) {
      toast.error(t('Failed to clone stream'));
    }
  };
  const exportFlows = async () => {
    try {
      const res = await axios.get(`${API}/campaigns/${campaignId}/streams/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'flows-' + campaignId + '.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      toast.error(t('Failed to export flows'));
    }
  };
  const onImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const res = await axios.post(`${API}/campaigns/${campaignId}/streams/import`, data);
      toast.success(t('Imported') + ': ' + ((res.data && res.data.imported) || 0));
      fetchStreams();
    } catch (_e) {
      toast.error(t('Failed to import flows'));
    }
  };
  const doReplaceUrl = async () => {
    if (!replaceFind) {
      toast.error(t('Enter the text to find'));
      return;
    }
    try {
      const res = await axios.post(`${API}/campaigns/${campaignId}/streams/replace-url`, { find: replaceFind, replace: replaceWith });
      toast.success(t('Streams changed') + ': ' + ((res.data && res.data.streams_changed) || 0));
      setReplaceOpen(false);
      setReplaceFind('');
      setReplaceWith('');
      fetchStreams();
    } catch (_e) {
      toast.error(t('Failed to replace URLs'));
    }
  };
  const equalizeWeights = async () => {
    try {
      await axios.post(`${API}/campaigns/${campaignId}/streams/equalize`);
      toast.success(t('Weights equalized'));
      fetchStreams();
    } catch (_e) {
      toast.error(t('Failed to equalize weights'));
    }
  };
  const openFavourites = async () => {
    setFavOpen(true);
    try {
      const res = await axios.get(`${API}/streams/favourites`);
      setFavStreams((res.data && res.data.streams) || []);
    } catch (_e) {
      setFavStreams([]);
    }
  };
  const insertFavourite = async (fav) => {
    try {
      const res = await axios.post(`${API}/campaigns/${campaignId}/streams/import`, { streams: [fav] });
      toast.success(t('Imported') + ': ' + ((res.data && res.data.imported) || 0));
      setFavOpen(false);
      fetchStreams();
    } catch (_e) {
      toast.error(t('Failed to insert flow'));
    }
  };
  const fetchStreams = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/campaigns/${campaignId}/streams`);
      setStreams(response.data);
    } catch (error) {
      console.error(error);
      toast.error(t('Failed to load streams'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLandingPages = async () => {
    try {
      const response = await axios.get(`${API}/landing-pages`);
      setLandingPages(response.data);
    } catch (error) { console.error(error); }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/offers`);
      setOffers(response.data);
    } catch (error) { console.error(error); }
  };

  const handleCreateStream = async () => {
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/streams`, formData);
      setStreams([...streams, response.data]);
      setCreateDialogOpen(false);
      resetForm();
      toast.success(t('Stream created'));
    } catch (error) {
      console.error(error);
      toast.error(t('Failed to create stream'));
    }
  };

  const handleUpdateStream = async () => {
    if (!editingStream) return;
    try {
      const response = await axios.put(`${API}/streams/${editingStream.id}`, formData);
      setStreams(streams.map((s) => (s.id === editingStream.id ? response.data : s)));
      setEditDialogOpen(false);
      setEditingStream(null);
      resetForm();
      toast.success(t('Stream updated'));
    } catch (error) {
      console.error(error);
      toast.error(t('Failed to update stream'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteStream = async (streamId) => {
    try {
      await axios.delete(`${API}/streams/${streamId}`);
      setStreams(streams.filter((s) => s.id !== streamId));
      setDeleteTarget(null);
      toast.success(t('Stream archived'));
    } catch (error) {
      console.error(error);
      toast.error(t('Failed to delete stream'));
    }
  };

  const toggleSplitTesting = async (val) => {
    if (!campaign) return;
    const prev = campaign.split_testing_enabled;
    setSavingSplit(true);
    setCampaign((c) => ({ ...c, split_testing_enabled: val }));
    try {
      await axios.put(`${API}/campaigns/${campaignId}`, { split_testing_enabled: val });
      toast.success(val ? t('Split testing enabled') : t('Split testing disabled'));
    } catch (error) {
      console.error(error);
      setCampaign((c) => ({ ...c, split_testing_enabled: prev }));
      toast.error(t('Failed to update campaign'));
    } finally {
      setSavingSplit(false);
    }
  };

  const resetForm = () => {
    setFormData(initialForm);
    setParamFiltersText('');
  };

  const openEditStream = (stream) => {
    setEditingStream(stream);
    setFormData({
      name: stream.name,
      stream_type: stream.stream_type,
      position: stream.position,
      weight: stream.weight,
      action_type: stream.action_type,
      action_options: stream.action_options || { redirect_url: '' },
      countries: stream.countries || [],
      cities: stream.cities || [],
      devices: stream.devices || [],
      browsers: stream.browsers || [],
      operating_systems: stream.operating_systems || [],
      languages: stream.languages || [],
      bot: stream.bot || null,
      proxy: stream.proxy || null,
      ip_version: stream.ip_version || null,
      connection_types: stream.connection_types || [],
      time_ranges: stream.time_ranges || [],
      date_ranges: stream.date_ranges || [],
      filter_relation: stream.filter_relation || 'and',
      countries_mode: stream.countries_mode || 'is',
      cities_mode: stream.cities_mode || 'is',
      devices_mode: stream.devices_mode || 'is',
      browsers_mode: stream.browsers_mode || 'is',
      operating_systems_mode: stream.operating_systems_mode || 'is',
      languages_mode: stream.languages_mode || 'is',
      connection_types_mode: stream.connection_types_mode || 'is',
      time_ranges_mode: stream.time_ranges_mode || 'is',
      date_ranges_mode: stream.date_ranges_mode || 'is',
      regions: stream.regions || [],
      regions_mode: stream.regions_mode || 'is',
      ip_addresses: stream.ip_addresses || [],
      ip_addresses_mode: stream.ip_addresses_mode || 'is',
      user_agents: stream.user_agents || [],
      user_agents_mode: stream.user_agents_mode || 'is',
      isps: stream.isps || [],
      isps_mode: stream.isps_mode || 'is',
      operators: stream.operators || [],
      operators_mode: stream.operators_mode || 'is',
      search_engines: stream.search_engines || [],
      search_engines_mode: stream.search_engines_mode || 'is',
      x_requested_with: stream.x_requested_with || [],
      x_requested_with_mode: stream.x_requested_with_mode || 'is',
      unique_clicks: stream.unique_clicks,
      click_limit: stream.click_limit,
      click_limit_per_hour: stream.click_limit_per_hour || null,
      click_limit_per_24h: stream.click_limit_per_24h || null,
      parameter_filters: stream.parameter_filters || {},
      parameter_filters_exclude: stream.parameter_filters_exclude || {},
      landing_page_ids: stream.landing_page_ids || [],
      offer_ids: stream.offer_ids || [],
      offer_selection_moment: stream.offer_selection_moment || 'after'
    });
    setParamFiltersText(
      paramFiltersToText(
        stream.parameter_filters || {},
        stream.parameter_filters_exclude || {}
      )
    );
    setEditDialogOpen(true);
  };

  const updateArrayField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: Array.isArray(value) ? value : value.split(',').map((i) => i.trim()).filter(Boolean)
    }));
  };

  // Compact IS / IS NOT switch rendered next to a filter label.
  const modeSelect = (field) => (
    <select
      value={formData[field] || 'is'}
      onChange={(e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }))}
      className="h-6 rounded border border-border bg-background px-1 text-xs text-muted-foreground"
    >
      <option value="is">{t('Is')}</option>
      <option value="is_not">{t('Is not')}</option>
    </select>
  );

  const addTimeRange = () =>
    setFormData((prev) => ({
      ...prev,
      time_ranges: [
        ...(prev.time_ranges || []),
        { start: '00:00', end: '23:59', days: [] },
      ],
    }));

  const updateTimeRange = (idx, field, value) =>
    setFormData((prev) => ({
      ...prev,
      time_ranges: (prev.time_ranges || []).map((r, i) =>
        i === idx ? { ...r, [field]: value } : r
      ),
    }));

  const removeTimeRange = (idx) =>
    setFormData((prev) => ({
      ...prev,
      time_ranges: (prev.time_ranges || []).filter((r, i) => i !== idx),
    }));
  const addDateRange = () =>
    setFormData((prev) => ({
      ...prev,
      date_ranges: [...(prev.date_ranges || []), { start: '', end: '' }],
    }));
  const updateDateRange = (idx, field, value) =>
    setFormData((prev) => ({
      ...prev,
      date_ranges: (prev.date_ranges || []).map((r, i) =>
        i === idx ? { ...r, [field]: value } : r
      ),
    }));
  const removeDateRange = (idx) =>
    setFormData((prev) => ({
      ...prev,
      date_ranges: (prev.date_ranges || []).filter((r, i) => i !== idx),
    }));

  const toggleTimeRangeDay = (idx, day) =>
    setFormData((prev) => ({
      ...prev,
      time_ranges: (prev.time_ranges || []).map((r, i) => {
        if (i !== idx) return r;
        const days = r.days || [];
        return {
          ...r,
          days: days.includes(day)
            ? days.filter((d) => d !== day)
            : [...days, day],
        };
      }),
    }));

  const getStreamTypeVariant = (type) => {
    switch (type) {
      case 'catch': return 'outline';
      case 'default': return 'secondary';
      case 'normal': return 'default';
      default: return 'outline';
    }
  };

  const getStreamTypeLabel = (type) => {
    switch (type) {
      case 'catch': return t('Catch');
      case 'default': return t('Fallback');
      case 'normal': return t('Normal');
      default: return type;
    }
  };

  // Rendered inline as a function (NOT as <StreamForm/>). Defining a
  // component inside Streams gives it a new identity on every keystroke, so
  // React would remount the inputs and the field would lose focus after one
  // character. Calling it as a function inlines the elements and preserves focus.
  const renderStreamForm = ({ includeFilters = true } = {}) => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Stream name')}</Label>
          <Input value={formData.name}
            onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={t('Stream name')} />
        </div>
        <div className="space-y-2">
          <Label>{t('Stream type')} <HelpHint text={t("Normal — a regular stream with filters. Catch (intercepting) — checked before Normal streams and takes a matching click first, e.g. bots or a test GEO. Fallback — the last stream; it gets traffic that matched nothing else.")} /></Label>
          <Select value={formData.stream_type}
            onValueChange={(value) => setFormData((prev) => ({ ...prev, stream_type: value }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">{t('Normal')}</SelectItem>
              <SelectItem value="catch">{t('Catch')}</SelectItem>
              <SelectItem value="default">{t('Fallback')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <details className="rounded-md border border-border p-3" data-testid="stream-order-weight">
        <summary className="cursor-pointer text-sm font-medium">{t('Order, weight and limits')}</summary>
        <p className="mt-1 text-xs text-muted-foreground">
          {campaign?.split_testing_enabled
            ? t('Split testing is on for this campaign: Normal streams share traffic by weight; position is not used.')
            : t('Split testing is off for this campaign: streams are checked by position and the first match takes the click. Weight is not used.')}
        </p>
        <div className="mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('Position')} <HelpHint text={t("Check priority. The lower the number, the earlier the stream is checked. Traffic goes to the first matching one.")} /></Label>
              <Input type="number" value={formData.position}
                onChange={(e) => setFormData((prev) => ({ ...prev, position: parseInt(e.target.value) || 0 }))}
                min="0" />
            </div>
            <div className="space-y-2">
              <Label>{t('Weight (%)')} <HelpHint text={t("When several streams share a type — the split of traffic between them. E.g. 70 and 30 = 70% and 30%.")} /></Label>
              <Input type="number" value={formData.weight}
                onChange={(e) => setFormData((prev) => ({ ...prev, weight: parseInt(e.target.value) || 0 }))}
                min="0" max="100" />
            </div>
            <div className="space-y-2">
              <Label>{t('Click limit')} <HelpHint text={t("Max clicks for this stream. Once reached, the stream stops taking traffic. Empty = no limit.")} /></Label>
              <Input type="number" value={formData.click_limit || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, click_limit: e.target.value ? parseInt(e.target.value) : null }))}
                placeholder={t('No limit')} />
            </div>
            <div className="space-y-2">
              <Label>{t('Clicks per hour')} <HelpHint text={t("Rolling-window limit: the stream stops taking NEW clicks after this many in the last hour, traffic falls through to the next stream. Empty = no limit.")} /></Label>
              <Input type="number" value={formData.click_limit_per_hour || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, click_limit_per_hour: e.target.value ? parseInt(e.target.value) : null }))}
                placeholder={t('No limit')} />
            </div>
            <div className="space-y-2">
              <Label>{t('Clicks per 24h')} <HelpHint text={t("Rolling-window limit over the last 24 hours. Empty = no limit.")} /></Label>
              <Input type="number" value={formData.click_limit_per_24h || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, click_limit_per_24h: e.target.value ? parseInt(e.target.value) : null }))}
                placeholder={t('No limit')} />
            </div>
          </div>
        </div>
      </details>

      <Separator />

      <div className="space-y-4">
        <Label className="text-base font-semibold">{t('Stream action')}</Label>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('Action type')} <HelpHint text={t("Redirect — send to the stream's landing/offer; Action — a special response (e.g. 404 / empty response for anti-fraud); Split test — split between variants.")} /></Label>
            <Select value={formData.action_type}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, action_type: value }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="redirect">{t('Redirect')}</SelectItem>
                <SelectItem value="action">{t('Action')}</SelectItem>
                <SelectItem value="split">{t('Split test')}</SelectItem>
                <SelectItem value="error_404">{t('Show 404')}</SelectItem>
                <SelectItem value="nothing">{t('Do nothing')}</SelectItem>
                <SelectItem value="white_page">{t('Show white page')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {formData.action_type === 'white_page' && (
            <p className="text-xs text-muted-foreground" data-testid="stream-white-page-hint">
              {t(
                'Shows the campaign white page (the page bots get) instead of an offer. Put it in a Default stream with no filters to send visitors that no other stream accepts to the white page instead of the target URL.'
              )}
            </p>
          )}
          {formData.action_type === 'redirect' && (
            <div className="space-y-2">
              <Label>{t('Redirect URL')}</Label>
              <Input value={formData.action_options.redirect_url || ''}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  action_options: { ...prev.action_options, redirect_url: e.target.value }
                }))}
                placeholder="https://example.com" />
            </div>
          )}
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <Label className="text-base font-semibold">{t('Landings and offers')}</Label>
        <div className="space-y-2">
          <Label>{t('Landings')}</Label>
          <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
            {landingPages.map((lp) => (
              <label key={lp.id} className="flex items-center gap-2 p-2 border border-border rounded cursor-pointer hover:bg-surface-2 text-sm">
                <input type="checkbox" checked={formData.landing_page_ids.includes(lp.id)}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    landing_page_ids: e.target.checked
                      ? [...prev.landing_page_ids, lp.id]
                      : prev.landing_page_ids.filter((id) => id !== lp.id)
                  }))}
                  className="rounded" />
                <span className="truncate">{lp.name}</span>
              </label>
            ))}
          </div>
          {landingPages.length === 0 && <p className="text-xs text-muted-foreground">{t('No landings available')}</p>}
        </div>
        <div className="space-y-2">
          <Label>{t('Offers')}</Label>
          <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
            {offers.map((offer) => (
              <label key={offer.id} className="flex items-center gap-2 p-2 border border-border rounded cursor-pointer hover:bg-surface-2 text-sm">
                <input type="checkbox" checked={formData.offer_ids.includes(offer.id)}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    offer_ids: e.target.checked
                      ? [...prev.offer_ids, offer.id]
                      : prev.offer_ids.filter((id) => id !== offer.id)
                  }))}
                  className="rounded" />
                <span className="truncate">{offer.name} ({offer.payout} {offer.currency})</span>
              </label>
            ))}
          </div>
          {offers.length === 0 && <p className="text-xs text-muted-foreground">{t('No offers available')}</p>}
        </div>
        <div className="space-y-2">
          <Label>{t('Offer selection moment')} <HelpHint text={t("When to pick the offer for this stream. After (default): at redirect time. Before: at landing-serve time, so {offer_name} / {offer_value:*} macros render on the landing page.")} /></Label>
          <select
            value={formData.offer_selection_moment || 'after'}
            onChange={(e) => setFormData((prev) => ({ ...prev, offer_selection_moment: e.target.value }))}
            className="h-9 w-full rounded border border-border bg-background px-2 text-sm"
          >
            <option value="after">{t('After click (at redirect)')}</option>
            <option value="before">{t('Before click (at landing — enables offer macros)')}</option>
          </select>
        </div>
      </div>

      {includeFilters && (
        <>
          <Separator />
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">{t('Traffic filters')}</Label>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">{t('Filter relation')} <HelpHint text={t("AND: the click must pass every configured filter. OR: any single passing filter admits the click.")} /></Label>
                <select
                  value={formData.filter_relation || 'and'}
                  onChange={(e) => setFormData((prev) => ({ ...prev, filter_relation: e.target.value }))}
                  className="h-9 rounded-md border border-border bg-background px-2 text-sm"
                >
                  <option value="and">{t('AND (all)')}</option>
                  <option value="or">{t('OR (any)')}</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t('Countries (ISO codes)')}</Label>
                  {modeSelect('countries_mode')}
                </div>
                <ListInput value={formData.countries}
                  onChange={(list) => updateArrayField('countries', list)}
                  placeholder={t('US, CA, GB, RU')} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t('Devices')}</Label>
                  {modeSelect('devices_mode')}
                </div>
                <ListInput value={formData.devices}
                  onChange={(list) => updateArrayField('devices', list)}
                  placeholder={t('Desktop, Mobile, Tablet')} />
              </div>
            </div>
            <details className="rounded-md border border-border p-3" data-testid="stream-more-filters">
              <summary className="cursor-pointer text-sm font-medium">{t('More filters')}</summary>
              <p className="mt-1 text-xs text-muted-foreground">{t('Cities, browsers, systems, languages, connection, regions, IP, user agents, operators, search engines, URL parameters, uniqueness, bots, proxy, IP version, schedule and dates.')}</p>
              <div className="mt-3 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('Cities')}</Label>
                      {modeSelect('cities_mode')}
                    </div>
                    <ListInput value={formData.cities}
                      onChange={(list) => updateArrayField('cities', list)}
                      placeholder={t('Moscow, New York, London')} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('Browsers')}</Label>
                      {modeSelect('browsers_mode')}
                    </div>
                    <ListInput value={formData.browsers}
                      onChange={(list) => updateArrayField('browsers', list)}
                      placeholder="Chrome, Firefox, Safari" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('Operating systems')}</Label>
                      {modeSelect('operating_systems_mode')}
                    </div>
                    <ListInput value={formData.operating_systems}
                      onChange={(list) => updateArrayField('operating_systems', list)}
                      placeholder="Windows, macOS, Linux, iOS, Android" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('Languages')}</Label>
                      {modeSelect('languages_mode')}
                    </div>
                    <ListInput value={formData.languages}
                      onChange={(list) => updateArrayField('languages', list)}
                      placeholder="en, ru, es, fr" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('Connection types')} <HelpHint text={t("Filter by network connection type. 'hosting' (datacenter/cloud) works out of the box; other values like cellular or wifi require the edge proxy to send an X-Connection-Type header.")} /></Label>
                      {modeSelect('connection_types_mode')}
                    </div>
                    <ListInput value={formData.connection_types}
                      onChange={(list) => updateArrayField('connection_types', list)}
                      placeholder="hosting, cellular, wifi" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('Regions')} <HelpHint text={t("GeoIP region / state / oblast names (e.g. California, Bavaria). Masks, /regex/ and @empty are supported.")} /></Label>
                      {modeSelect('regions_mode')}
                    </div>
                    <ListInput value={formData.regions}
                      onChange={(list) => updateArrayField('regions', list)}
                      placeholder={t('California, Bavaria, Moscow Oblast')} />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('IP addresses')} <HelpHint text={t("Exact IPs, CIDR blocks (10.0.0.0/8) or masks (192.168.*). Handy for allow-listing your own test IPs or blocking a datacenter range.")} /></Label>
                      {modeSelect('ip_addresses_mode')}
                    </div>
                    <ListInput value={formData.ip_addresses}
                      onChange={(list) => updateArrayField('ip_addresses', list)}
                      placeholder="203.0.113.5, 10.0.0.0/8" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('User agents')} <HelpHint text={t("Substrings, masks or /regex/ matched against the raw User-Agent — e.g. *Chrome/12* targets Chrome 120+, HeadlessChrome catches headless browsers.")} /></Label>
                      {modeSelect('user_agents_mode')}
                    </div>
                    <ListInput value={formData.user_agents}
                      onChange={(list) => updateArrayField('user_agents', list)}
                      placeholder="*Chrome/12*, HeadlessChrome" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('ISP / carrier')} <HelpHint text={t("Match the visitor's ISP / ASN organisation name (from the offline IP-intel table). Masks, /regex/ and @empty supported — e.g. AMAZON*, *Comcast*.")} /></Label>
                      {modeSelect('isps_mode')}
                    </div>
                    <ListInput value={formData.isps}
                      onChange={(list) => updateArrayField('isps', list)}
                      placeholder="Comcast, AMAZON*" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('Mobile operator / carrier')} <HelpHint text={t("Match the mobile carrier from the edge X-Carrier header. Masks, /regex/ and @empty supported — e.g. Vodafone*, /^T-Mobile/.")} /></Label>
                      {modeSelect('operators_mode')}
                    </div>
                    <ListInput value={formData.operators}
                      onChange={(list) => updateArrayField('operators', list)}
                      placeholder="Vodafone, MTS, Orange*" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('Search engines')} <HelpHint text={t("Match the search engine derived from the se / se_referrer params or the referrer host — e.g. google, bing, yandex.")} /></Label>
                      {modeSelect('search_engines_mode')}
                    </div>
                    <ListInput value={formData.search_engines}
                      onChange={(list) => updateArrayField('search_engines', list)}
                      placeholder="google, bing, yandex" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t('X-Requested-With')} <HelpHint text={t("Match the X-Requested-With header for webview / in-app browser detection. Substring match — e.g. com.android targets Android webviews; @empty targets clicks with no header.")} /></Label>
                      {modeSelect('x_requested_with_mode')}
                    </div>
                    <ListInput value={formData.x_requested_with}
                      onChange={(list) => updateArrayField('x_requested_with', list)}
                      placeholder="com.android, XMLHttpRequest" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('Parameter filters')} <HelpHint text={t("Route by URL sub-parameters. One rule per line: name=value1, value2 allows listed values; name!=value1, value2 blocks them (a missing parameter passes a block rule). A rule without values adds no constraint.")} /></Label>
                  <Textarea
                    value={paramFiltersText}
                    onChange={(e) => {
                      setParamFiltersText(e.target.value);
                      const parsed = textToParamFilters(e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        parameter_filters: parsed.include,
                        parameter_filters_exclude: parsed.exclude,
                      }));
                    }}
                    placeholder={"sub1=gold, silver\nutm_source!=bot-net"}
                    className="font-mono text-sm min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('Unique clicks')} <HelpHint text={t("Filter by visit uniqueness: unique only / repeat only / do not filter.")} /></Label>
                  <Select value={formData.unique_clicks || ''}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, unique_clicks: value || null }))}>
                    <SelectTrigger><SelectValue placeholder={t("Don't filter")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">{t("Don't filter")}</SelectItem>
                      <SelectItem value="accept">{t('Accept only unique')}</SelectItem>
                      <SelectItem value="filter">{t('Filter unique')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('Bots')} <HelpHint text={t("How to handle bots (detected by User-Agent): do not filter / bots only / exclude bots. Handy for an anti-fraud stream.")} /></Label>
                    <Select value={formData.bot || 'any'}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, bot: value === 'any' ? null : value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t('Any')}</SelectItem>
                        <SelectItem value="only">{t('Only bots')}</SelectItem>
                        <SelectItem value="exclude">{t('Exclude bots')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('Proxy / VPN')} <HelpHint text={t("Proxy/VPN filter (datacenter/Tor): do not filter / proxy only / exclude proxy.")} /></Label>
                    <Select value={formData.proxy || 'any'}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, proxy: value === 'any' ? null : value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t('Any')}</SelectItem>
                        <SelectItem value="only">{t('Only proxy')}</SelectItem>
                        <SelectItem value="exclude">{t('Exclude proxy')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('IP version')} <HelpHint text={t("Allow only IPv4 or only IPv6, or do not filter.")} /></Label>
                    <Select value={formData.ip_version || 'any'}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, ip_version: value === 'any' ? null : value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">{t('Any')}</SelectItem>
                        <SelectItem value="v4">IPv4</SelectItem>
                        <SelectItem value="v6">IPv6</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{t('Schedule')} <HelpHint text={t("Days of the week and hours when the stream is active. Time is in the campaign owner's time zone. 'Is not' inverts the schedule: the stream accepts clicks EXCEPT at the listed times.")} /></Label>
                    <div className="flex items-center gap-2">
                      {modeSelect('time_ranges_mode')}
                      <Button type="button" variant="outline" size="sm" onClick={addTimeRange}>
                        <Plus className="w-4 h-4 mr-1" />
                        {t('Add interval')}
                      </Button>
                    </div>
                  </div>
                  {(formData.time_ranges || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t('Every day, all day')}</p>
                  ) : (
                    <div className="space-y-3">
                      {(formData.time_ranges || []).map((r, idx) => (
                        <div key={idx} className="border border-border rounded p-3 space-y-2">
                          <div className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={r.start || '00:00'}
                              onChange={(e) => updateTimeRange(idx, 'start', e.target.value)}
                              className="w-32"
                            />
                            <span className="text-muted-foreground">–</span>
                            <Input
                              type="time"
                              value={r.end || '23:59'}
                              onChange={(e) => updateTimeRange(idx, 'end', e.target.value)}
                              className="w-32"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="ml-auto"
                              aria-label={t('Remove time range')}
                              title={t('Remove time range')}
                              onClick={() => removeTimeRange(idx)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {DOW.map(([label, value]) => (
                              <button
                                key={value}
                                type="button"
                                onClick={() => toggleTimeRangeDay(idx, value)}
                                className={
                                  'px-2 py-1 rounded text-xs border transition-colors ' +
                                  ((r.days || []).includes(value)
                                    ? 'border-primary bg-primary/10 text-primary'
                                    : 'border-border text-muted-foreground hover:text-foreground')
                                }
                              >
                                {t(label)}
                              </button>
                            ))}
                          </div>
                          {(r.days || []).length === 0 ? (
                            <p className="text-xs text-muted-foreground">{t('Every day')}</p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{t('Date range')} <HelpHint text={t("Calendar dates when the stream is active (owner timezone). Leave start or end empty for an open-ended side. 'Is not' inverts the range.")} /></Label>
                    <div className="flex items-center gap-2">
                      {modeSelect('date_ranges_mode')}
                      <Button type="button" variant="outline" size="sm" onClick={addDateRange}>
                        <Plus className="w-4 h-4 mr-1" />
                        {t('Add range')}
                      </Button>
                    </div>
                  </div>
                  {(formData.date_ranges || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t('Any date')}</p>
                  ) : (
                    <div className="space-y-2">
                      {(formData.date_ranges || []).map((r, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <Input type="date" value={r.start || ''} onChange={(e) => updateDateRange(idx, 'start', e.target.value)} className="w-40" />
                          <span className="text-muted-foreground">–</span>
                          <Input type="date" value={r.end || ''} onChange={(e) => updateDateRange(idx, 'end', e.target.value)} className="w-40" />
                          <Button type="button" variant="ghost" size="sm" className="ml-auto" aria-label={t('Remove date range')} title={t('Remove date range')} onClick={() => removeDateRange(idx)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </details>
          </div>
        </>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">{t('Campaign not found')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-[-0.03em] text-foreground">{t('Campaign streams')}</h1>
          <p className="text-muted-foreground mt-1">{campaign.name}</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5">
            <Switch
              id="split-testing"
              checked={!!campaign.split_testing_enabled}
              onCheckedChange={toggleSplitTesting}
              disabled={savingSplit}
            />
            <Label htmlFor="split-testing" className="text-sm cursor-pointer whitespace-nowrap">
              {t('Split testing')}
              <HelpHint text={t("Flow rotation. On: Normal streams rotate by weight, position is ignored. Off: streams are checked top-down by position and the first match takes all the traffic. Catch streams always intercept first and never rotate by weight.")} />
            </Label>
          </div>
          <input type="file" accept="application/json,.json" ref={fileInputRef} onChange={onImportFile} className="hidden" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline"><ListChecks className="w-4 h-4 mr-2" />{t('Flow tools')}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); exportFlows(); }}><Download className="w-4 h-4 mr-2" />{t('Export flows')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); if (fileInputRef.current) fileInputRef.current.click(); }}><Upload className="w-4 h-4 mr-2" />{t('Import flows')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setReplaceOpen(true); }}><RefreshCw className="w-4 h-4 mr-2" />{t('Replace URL')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); equalizeWeights(); }}>{t('Equalize weights')}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); openFavourites(); }}><Star className="w-4 h-4 mr-2" />{t('Favourites')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button asChild variant="outline">
            <Link to={`/campaigns/${campaignId}/ai-optimize`}>
              <Brain className="w-4 h-4 mr-2" />
              {t('AI Optimizer')}
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={`/campaigns/${campaignId}/simulate`} data-testid="streams-simulate-link">
              {t('Simulate')}
            </Link>
          </Button>
          <Button asChild variant="outline" data-testid="streams-view-archived">
            <Link to="/archive">
              <Archive className="w-4 h-4 mr-2" />
              {t('View archived')}
            </Link>
          </Button>
          <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                {t('Create stream')}
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('Create stream')}</DialogTitle>
              <DialogDescription>{t('Configure stream for traffic distribution by conditions')}</DialogDescription>
            </DialogHeader>

            {renderStreamForm({ includeFilters: true })}

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>{t('Cancel')}</Button>
              <Button onClick={handleCreateStream}>{t('Create')}</Button>
            </div>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-4">
        {streams.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <XpnsrEmpty
                icon={Filter}
                title={t('No streams')}
                subtitle={t('Create streams to split traffic by geo, device, OS, or custom conditions.')}
              />
            </CardContent>
          </Card>
        ) : (
          streams.map((stream) => (
            <Card key={stream.id} className="border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-surface-2 rounded-full flex items-center justify-center text-primary font-semibold text-sm">
                        {stream.position + 1}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{stream.name}</CardTitle>
                        <div className="flex items-center space-x-2 mt-1">
                          <Badge variant={getStreamTypeVariant(stream.stream_type)}>
                            {getStreamTypeLabel(stream.stream_type)}
                          </Badge>
                          <Badge variant={stream.is_active ? 'default' : 'secondary'}>
                            {stream.is_active ? t('Active') : t('Inactive')}
                          </Badge>
                          <Badge variant="outline">{t('Weight')}: {stream.weight}%</Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm" aria-label={stream.is_active ? t('Pause stream') : t('Resume stream')} title={stream.is_active ? t('Pause stream') : t('Resume stream')}>
                      {stream.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => toggleFavourite(stream)} title={t('Favourite')} aria-label={t('Favourite')}>
                      <Star className={'w-4 h-4 ' + (stream.is_favourite ? 'fill-primary text-primary' : '')} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => cloneStream(stream)} title={t('Clone')} aria-label={t('Clone')}>
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" aria-label={t('Edit stream')} title={t('Edit stream')} onClick={() => openEditStream(stream)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" aria-label={t('Delete stream')} title={t('Delete stream')} onClick={() => setDeleteTarget(stream)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Clicks')}</Label>
                    <p>{stream.total_clicks}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Unique')}</Label>
                    <p>{stream.unique_clicks_count}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Bots filtered')}</Label>
                    <p>{stream.bots_filtered}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">{t('Action type')}</Label>
                    <p className="capitalize">{stream.action_type}</p>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex flex-wrap gap-2">
                    {stream.countries && stream.countries.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        <Globe className="w-3 h-3 mr-1" />
                        {t('Countries')}: {stream.countries.join(', ')}
                      </Badge>
                    )}
                    {stream.devices && stream.devices.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        <Smartphone className="w-3 h-3 mr-1" />
                        {t('Devices')}: {stream.devices.join(', ')}
                      </Badge>
                    )}
                    {stream.browsers && stream.browsers.length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        <Globe className="w-3 h-3 mr-1" />
                        {t('Browsers')}: {stream.browsers.join(', ')}
                      </Badge>
                    )}
                    {stream.click_limit && (
                      <Badge variant="outline" className="text-xs">
                        {t('Limit')}: {stream.click_limit}
                      </Badge>
                    )}
                    {stream.parameter_filters && Object.keys(stream.parameter_filters).length > 0 && (
                      <Badge variant="outline" className="text-xs">
                        <Filter className="w-3 h-3 mr-1" />
                        {t('Params')}: {Object.keys(stream.parameter_filters).join(', ')}
                      </Badge>
                    )}
                  </div>

                  {stream.action_type === 'redirect' && stream.action_options?.redirect_url && (
                    <div className="mt-3">
                      <Label className="text-xs text-muted-foreground">{t('Redirect URL')}</Label>
                      <p className="text-sm font-mono bg-surface-2 p-2 rounded mt-1 truncate">
                        {stream.action_options.redirect_url}
                      </p>
                    </div>
                  )}
                  {stream.landing_page_ids?.length > 0 && (
                    <div className="mt-2">
                      <Label className="text-xs text-muted-foreground">{t('Landings')}</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {stream.landing_page_ids.map((id) => {
                          const lp = landingPages.find((l) => l.id === id);
                          return <Badge key={id} variant="outline" className="text-xs">{lp?.name || id.slice(0, 8)}</Badge>;
                        })}
                      </div>
                    </div>
                  )}
                  {stream.offer_ids?.length > 0 && (
                    <div className="mt-2">
                      <Label className="text-xs text-muted-foreground">{t('Offers')}</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {stream.offer_ids.map((id) => {
                          const o = offers.find((of) => of.id === id);
                          return <Badge key={id} variant="outline" className="text-xs">{o?.name || id.slice(0, 8)}</Badge>;
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) { setEditingStream(null); resetForm(); } }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Edit stream')}</DialogTitle>
            <DialogDescription>{t('Modify stream settings')}</DialogDescription>
          </DialogHeader>

          {/* Show the full traffic-filters block when editing too — openEditStream
              loads every filter into formData, so hiding them here just meant an
              operator couldn't see/change geo/device/etc. after creating a stream. */}
          {renderStreamForm({ includeFilters: true })}

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={handleUpdateStream}>{t('Save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Archive stream')}
        description={t('This will move "{name}" to the archive. You can restore it or delete it permanently from the Archive page.').replace('{name}', deleteTarget?.name || '')}
        confirmLabel={t('Archive')}
        variant="default"
        onConfirm={() => handleDeleteStream(deleteTarget?.id)}
      />

      <Dialog open={replaceOpen} onOpenChange={setReplaceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('Replace URL')}</DialogTitle>
            <DialogDescription>{t('Find and replace a substring in flow redirect URLs (never landing/offer URLs).')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t('Find')}</Label>
              <Input value={replaceFind} onChange={(e) => setReplaceFind(e.target.value)} placeholder="old.example.com" />
            </div>
            <div className="space-y-1">
              <Label>{t('Replace with')}</Label>
              <Input value={replaceWith} onChange={(e) => setReplaceWith(e.target.value)} placeholder="new.example.com" />
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setReplaceOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={doReplaceUrl}>{t('Replace')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={favOpen} onOpenChange={setFavOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Favourite flows')}</DialogTitle>
            <DialogDescription>{t('Starred flows from any campaign. Insert a copy into this campaign.')}</DialogDescription>
          </DialogHeader>
          {favStreams.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">{t('No favourite flows yet. Star a flow to reuse it here.')}</p>
          ) : (
            <div className="space-y-1">
              {favStreams.map((fav) => (
                <div key={fav.id} className="flex items-center justify-between gap-2 border border-border rounded px-2 py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Star className="w-4 h-4 fill-primary text-primary shrink-0" />
                    <span className="text-sm truncate">{fav.name}</span>
                  </div>
                  <Button variant="outline" size="sm" className="shrink-0" onClick={() => insertFavourite(fav)}>
                    <Plus className="w-4 h-4 mr-1" />{t('Insert')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
